/**
 * Research Data section types and data
 * Data is pre-sorted by order value for display
 */

export interface GeographyOption {
    label: string;
    url: string;
}

export interface DataTypeOption {
    dataType: string;
    geographies: GeographyOption[];
}

export interface ResearchDataSection {
    title: string;
    /** Array of HTML paragraph content strings */
    paragraphs: string[];
    order: number;
    dataTypeLabel: string;
    geographyLabel: string;
    dropdownOptions: DataTypeOption[];
}

/**
 * All research data sections, pre-sorted by order value
 */
export const RESEARCH_DATA_SECTIONS: ResearchDataSection[] = [
    // Order 1: HOME VALUES
    {
        title: 'Home values',
        paragraphs: [
            `<b>Zillow Home Value Index (ZHVI): </b>A measure of the typical home value and market changes across a given region and housing type. It reflects the typical value for homes in the 35th to 65th percentile range. Available as a smoothed, seasonally adjusted measure and as a raw measure.`,
            `Zillow publishes top-tier ZHVI ($, typical value for homes within the 65th to 95th percentile range for a given region) and bottom-tier ZHVI ($, typical value for homes within the 5th to 35th percentile range for a given region).`,
            `Zillow also publishes ZHVI for all single-family residences ($, typical value for all single-family homes in a given region), for condo/coops ($), for all homes with 1, 2, 3, 4 and 5+ bedrooms ($).`,
            `<b>Note: </b>Starting with the January 2023 data release, and for all subsequent releases, the full ZHVI time series has been upgraded to harness the power of the <a href="https://zillow.mediaroom.com/2021-06-15-Zillow-Launches-New-Neural-Zestimate,-Yielding-Major-Accuracy-Gains">neural Zestimate</a>.`,
            `More information about what ZHVI is and how it's calculated is available on <a href="https://www.zillow.com/research/methodology-neural-zhvi-32128" target="_blank" rel="noopener">this overview page</a>. Here's a handy <a href="https://www.zillow.com/research/zhvi-user-guide/">ZHVI User Guide</a> for information about properly citing and making calculations with this metric.`,
            `<b>Mortgage Payment: </b>An estimate of the monthly mortgage payment on a new home purchase with the average interest rate of that month. The home value is estimated using smoothed and seasonally adjusted ZHVI. If the down payment is less than 20%, the monthly mortgage payment includes 1% mortgage insurance.`,
            `<b>Total Monthly Payment: </b>An estimate of the total monthly payment on a new home purchase with current interest rates. The total monthly payment includes the mortgage payment, homeowner's insurance, property taxes, and maintenance costs worth 0.5% of the home's value. The home value is estimated using smoothed and seasonally adjusted ZHVI. If the down payment is less than 20%, the monthly mortgage payment includes 1% mortgage insurance. Homeowners insurance rates and property tax rate estimates vary by region.`,
        ],
        order: 1,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType:
                    'ZHVI All Homes (SFR, Condo/Co-op) Time Series, Smoothed, Seasonally Adjusted($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'ZHVI All Homes (SFR, Condo/Co-op) Time Series, Raw, Mid-Tier ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_uc_sfrcondo_tier_0.33_0.67_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI All Homes- Top Tier Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_uc_sfrcondo_tier_0.67_1.0_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_uc_sfrcondo_tier_0.67_1.0_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_uc_sfrcondo_tier_0.67_1.0_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_uc_sfrcondo_tier_0.67_1.0_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI All Homes- Bottom Tier Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_uc_sfrcondo_tier_0.0_0.33_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_uc_sfrcondo_tier_0.0_0.33_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_uc_sfrcondo_tier_0.0_0.33_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_uc_sfrcondo_tier_0.0_0.33_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI Single-Family Homes Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_uc_sfr_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_uc_sfr_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_uc_sfr_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_uc_sfr_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_uc_sfr_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_uc_sfr_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI Condo/Co-op Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_uc_condo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_uc_condo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_uc_condo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_uc_condo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_uc_condo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_uc_condo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI 1-Bedroom Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_bdrmcnt_1_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_bdrmcnt_1_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_bdrmcnt_1_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_bdrmcnt_1_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_bdrmcnt_1_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_bdrmcnt_1_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI 2-Bedroom Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_bdrmcnt_2_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_bdrmcnt_2_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_bdrmcnt_2_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_bdrmcnt_2_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_bdrmcnt_2_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_bdrmcnt_2_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI 3-Bedroom Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_bdrmcnt_3_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_bdrmcnt_3_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_bdrmcnt_3_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_bdrmcnt_3_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_bdrmcnt_3_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_bdrmcnt_3_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI 4-Bedroom Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_bdrmcnt_4_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_bdrmcnt_4_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_bdrmcnt_4_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_bdrmcnt_4_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_bdrmcnt_4_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_bdrmcnt_4_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZHVI 5+ Bedroom Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Metro_zhvi_bdrmcnt_5_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'State',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/State_zhvi_bdrmcnt_5_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/County_zhvi_bdrmcnt_5_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/City_zhvi_bdrmcnt_5_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Zip_zhvi_bdrmcnt_5_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'Neighborhood',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvi/Neighborhood_zhvi_bdrmcnt_5_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Mortgage Payment: 20% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mortgage_payment/Metro_mortgage_payment_downpayment_0.20_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Mortgage Payment: 10% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mortgage_payment/Metro_mortgage_payment_downpayment_0.10_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Mortgage Payment: 5% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mortgage_payment/Metro_mortgage_payment_downpayment_0.05_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Total Monthly Payment: 20% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/total_monthly_payment/Metro_total_monthly_payment_downpayment_0.20_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Total Monthly Payment: 10% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/total_monthly_payment/Metro_total_monthly_payment_downpayment_0.10_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Total Monthly Payment: 5% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/total_monthly_payment/Metro_total_monthly_payment_downpayment_0.05_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
        ],
    },
    // Order 2: HOME VALUES FORECASTS
    {
        title: 'Home values forecasts',
        paragraphs: [
            `<b>Zillow Home Value Forecast (ZHVF): </b>A month-ahead, quarter-ahead and year-ahead forecast of the Zillow Home Value Index (ZHVI). ZHVF is created using the all homes, mid-tier cut of ZHVI and is available both raw and smoothed, seasonally adjusted.`,
            `<b>Note: </b>Starting with the January 2023 forecast (made available in February 2023), Zillow's Home Value Forecast is based on the upgraded ZHVI that harnesses the power of the <a href="https://zillow.mediaroom.com/2021-06-15-Zillow-Launches-New-Neural-Zestimate,-Yielding-Major-Accuracy-Gains">neural Zestimate</a>. More information about what ZHVI is and how it's calculated is available on <a href="https://www.zillow.com/research/methodology-neural-zhvi-32128" target="_blank" rel="noopener">this overview page</a>.`,
        ],
        order: 2,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType:
                    'ZHVF (Forecast), All Homes (SFR, Condo/Co-op), Smoothed, Seasonally Adjusted, Mid-Tier (MoM%, QoQ%, YoY%)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvf_growth/Metro_zhvf_growth_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvf_growth/Zip_zhvf_growth_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'ZHVF (Forecast), All Homes (SFR, Condo/Co-op), Raw, Mid-Tier (MoM%, QoQ%, YoY%)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvf_growth/Metro_zhvf_growth_uc_sfrcondo_tier_0.33_0.67_month.csv',
                    },
                    {
                        label: 'ZIP Code',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zhvf_growth/Zip_zhvf_growth_uc_sfrcondo_tier_0.33_0.67_month.csv',
                    },
                ],
            },
        ],
    },
    // Order 3: RENTALS
    {
        title: 'Rentals',
        paragraphs: [
            `<b>Zillow Observed Rent Index (ZORI): </b>A smoothed measure of the typical observed market rate rent across a given region. ZORI is a repeat-rent index that is weighted to the rental housing stock to ensure representativeness across the entire market, not just those homes currently listed for-rent. The index is dollar-denominated by computing the mean of listed rents that fall into the 35th to 65th percentile range for all homes and apartments in a given region, which is weighted to reflect the rental housing stock.`,
            `ZORI is created for three different categories: All homes, Single Family Residences, and Multi-Family Residences. For more detailed information, you can refer to the <a href="https://www.zillow.com/research/methodology-zori-repeat-rent-27092/">ZORI methodology.</a>`,
            `<b>Zillow Observed Renter Demand Index (ZORDI): </b>A measure of the typical observed rental market engagement across a region. ZORDI tracks engagement on Zillow's rental listings to proxy changes in rental demand. The metric is smoothed to remove volatility.`,
            `ZORDI is created for different categories including All homes, Single Family Residences, Condo and Multi-Family Residences at national and MSA levels.`,
        ],
        order: 3,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType:
                    'ZORI (Smoothed): All Homes Plus Multifamily Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Metro_zori_uc_sfrcondomfr_sm_month.csv',
                    },
                    {
                        label: 'ZIP Codes',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Zip_zori_uc_sfrcondomfr_sm_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/County_zori_uc_sfrcondomfr_sm_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/City_zori_uc_sfrcondomfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'ZORI (Smoothed, Seasonally Adjusted): All Homes Plus Multifamily Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Metro_zori_uc_sfrcondomfr_sm_sa_month.csv',
                    },
                    {
                        label: 'ZIP Codes',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Zip_zori_uc_sfrcondomfr_sm_sa_month.csv',
                    },
                    {
                        label: 'County',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/County_zori_uc_sfrcondomfr_sm_sa_month.csv',
                    },
                    {
                        label: 'City',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/City_zori_uc_sfrcondomfr_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'ZORI (Smoothed): Single Family Residence Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Metro_zori_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'ZORI (Smoothed, Seasonally Adjusted): Single Family Residence Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Metro_zori_uc_sfr_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'ZORI (Smoothed): Multi Family Residence Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Metro_zori_uc_mfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'ZORI (Smoothed, Seasonally Adjusted): Multi Family Residence Time Series ($)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zori/Metro_zori_uc_mfr_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZORDI: All Homes Plus Multifamily Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zordi/Metro_zordi_uc_sfrcondomfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZORDI: Single Family Residence Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zordi/Metro_zordi_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZORDI: Condo/Co-op Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zordi/Metro_zordi_uc_condo_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZORDI: Multifamily Residence Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zordi/Metro_zordi_uc_mfr_month.csv',
                    },
                ],
            },
        ],
    },
    // Order 4: RENTAL FORECASTS
    {
        title: 'Rental forecasts',
        paragraphs: [
            `<b>Zillow Observed Rent Forecast (ZORF): </b>A month-ahead, quarter-ahead and year-ahead forecast of the Zillow Observed Rent Index (ZORI).`,
        ],
        order: 4,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType: 'ZORF, (Single Family Residence, Smoothed)',
                geographies: [
                    {
                        label: 'U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zorf_growth/National_zorf_growth_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'ZORF, (Multi Family Residence, Smoothed)',
                geographies: [
                    {
                        label: 'U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/zorf_growth/National_zorf_growth_uc_mfr_sm_month.csv',
                    },
                ],
            },
        ],
    },
    // Order 5: FOR-SALE LISTINGS
    {
        title: 'For-sale listings',
        paragraphs: [
            `<b>For-Sale Inventory: </b>The count of unique listings that were active at any time in a given month.`,
            `<b>New Listings: </b>Indicates how many new listings have come on the market in a given month.`,
            `<b>Newly Pending Listings: </b>The count of listings that changed from for-sale to pending status on <a href="http://zillow.com/" target="_blank" rel="noreferrer noopener">Zillow.com</a> in a given time period.`,
            `<b>Median List Price: </b>The median price at which homes across various geographies were listed.`,
            `<b>Note:</b> "Weekly" means you can see the metric's value on a weekly cadence. The CSVs are updated monthly.`,
        ],
        order: 5,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType: 'For-Sale Inventory (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'For-Sale Inventory (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'For-Sale Inventory (Smooth, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'For-Sale Inventory (Smooth, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'For-Sale Inventory (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'For-Sale Inventory (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'For-Sale Inventory (Raw, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'For-Sale Inventory (Raw, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/invt_fs/Metro_invt_fs_uc_sfr_week.csv',
                    },
                ],
            },
            {
                dataType: 'New Listings (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_listings/Metro_new_listings_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'New Listings (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_listings/Metro_new_listings_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'New Listings (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_listings/Metro_new_listings_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'New Listings (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_listings/Metro_new_listings_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Newly Pending Listings (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_pending/Metro_new_pending_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Newly Pending Listings (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_pending/Metro_new_pending_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Newly Pending Listings (Smoothed, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_pending/Metro_new_pending_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Newly Pending Listings (Smoothed, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_pending/Metro_new_pending_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Smooth, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Smooth, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Raw, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median List Price (Raw, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mlp/Metro_mlp_uc_sfr_week.csv',
                    },
                ],
            },
        ],
    },
    // Order 5: SALES (same order as FOR-SALE LISTINGS, follows in sequence)
    {
        title: 'Sales',
        paragraphs: [
            `<b>Sales count (nowcast): </b>The estimated number of unique properties that sold during the month. For sales count nowcast, the latest month's number is the estimation after accounting for the latency between when sales occur and when they are reported. Available only for the raw cut for all homes.`,
            `<b>Sale Price (median/mean): </b>The price at which homes across various geographies were sold. The latest month's number is the estimation after accounting for the latency between when sales occur and when they are reported. Available only for the raw cut for all homes.`,
            `<b>Total Transaction Value: </b>The total dollar value of all homes sold in a given period (mean sale price x sales count). The latest month's number is the estimation after accounting for the latency between when sales occur and when they are reported. Available only for the raw cut for all homes.`,
            `<b>Sale-to-List Ratio (mean/median): </b>Ratio of sale vs. final list price.`,
            `<b>Percent of Sales Below/Above List: </b>Share of sales where sale price below/above the final list price; excludes homes sold for exactly the list price.`,
            `<b>Note:</b> "Weekly" means the metric's value shows weekly details; CSVs are updated monthly. The Sales Count Nowcast, Sale Price, and Total Transaction Value are produced at the end of the second week. Data is published on the 16th of the month. (Example: On July 16th, we will estimate the June numbers. We do this due to sales data latency).`,
        ],
        order: 5,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType: 'Sales Count (Nowcast, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/sales_count_now/Metro_sales_count_now_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Sales Count (Raw, SFR only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/sales_count_raw/Metro_sales_count_raw_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Sales Count (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/sales_count_raw/Metro_sales_count_raw_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Sales Count (Smooth, SFR only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/sales_count_raw/Metro_sales_count_raw_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Sale Price (Nowcast, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_price_now/Metro_median_sale_price_now_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Sale Price (Raw, SFR only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_price/Metro_median_sale_price_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Sale Price (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_price/Metro_median_sale_price_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Sale Price (Smooth, SFR only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_price/Metro_median_sale_price_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Sale-to-List Ratio (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_to_list/Metro_mean_sale_to_list_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Mean Sale-to-List Ratio (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_to_list/Metro_mean_sale_to_list_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Sale-to-List Ratio (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_to_list/Metro_mean_sale_to_list_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Sale-to-List Ratio (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_to_list/Metro_mean_sale_to_list_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Sale-to-List Ratio (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_to_list/Metro_median_sale_to_list_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Median Sale-to-List Ratio (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_to_list/Metro_median_sale_to_list_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Sale-to-List Ratio (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_to_list/Metro_median_sale_to_list_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Median Sale-to-List Ratio (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_sale_to_list/Metro_median_sale_to_list_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Above List (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_above_list/Metro_pct_sold_above_list_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Above List (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_above_list/Metro_pct_sold_above_list_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Above List (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_above_list/Metro_pct_sold_above_list_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Above List (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_above_list/Metro_pct_sold_above_list_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Below List (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_below_list/Metro_pct_sold_below_list_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Below List (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_below_list/Metro_pct_sold_below_list_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Below List (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_below_list/Metro_pct_sold_below_list_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Percent of Homes Sold Below List (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/pct_sold_below_list/Metro_pct_sold_below_list_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Sale Price (Nowcast, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_price_now/Metro_mean_sale_price_now_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Mean Sale Price (Smooth & Seasonally Adjusted, SFR only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_price/Metro_mean_sale_price_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Sale Price (Raw, SFR only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_price/Metro_mean_sale_price_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Mean Sale Price (Smooth & Seasonally Adjusted, SFR only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_price/Metro_mean_sale_price_uc_sfr_sm_sa_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Sale Price (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_price/Metro_mean_sale_price_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Sale Price (Smooth, SFR only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_sale_price/Metro_mean_sale_price_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Total Transaction Value (Nowcast, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/total_transaction_value_now/Metro_total_transaction_value_now_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Total Transaction Value (Raw, SFR only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/total_transaction_value/Metro_total_transaction_value_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Total Transaction Value (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/total_transaction_value/Metro_total_transaction_value_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Total Transaction Value (Smooth, SFR only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/total_transaction_value/Metro_total_transaction_value_uc_sfr_sm_week.csv',
                    },
                ],
            },
        ],
    },
    // Order 6: DAYS ON MARKET AND PRICE CUTS
    {
        title: 'Days on market and price cuts',
        paragraphs: [
            `<b>Days to Pending: </b>How long it takes homes in a region to change to pending status on <a href="http://zillow.com/" target="_blank" rel="noopener noreferrer">Zillow.com</a> after first being shown as for sale. The reported figure indicates the number of days (mean or median) that it took for homes that went pending during the week being reported, to go pending. This differs from the old "Days on Zillow" metric in that it excludes the in-contract period before a home sells.`,
            `<b>Days to Close (mean/median): </b>Number of days between the listing going pending and the sale date.`,
            `<b>Share of Listings With a Price Cut: </b>The number of unique properties with a list price at the end of the month that's less than the list price at the beginning of the month, divided by the number of unique properties with an active listing at some point during the month.`,
            `<b>Price Cuts: </b>The <b>mean</b> and <b>median</b> price cut for listings in a given region during a given time period, expressed as both dollars ($) and as a percentage (%) of list price.`,
            `<b>Note:</b> "Weekly" means you can see the metric's value on a weekly cadence. The CSVs are updated monthly.`,
        ],
        order: 6,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType: 'Mean Days to Pending (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_doz_pending/Metro_mean_doz_pending_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Days to Pending (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_doz_pending/Metro_mean_doz_pending_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Days to Pending (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_doz_pending/Metro_mean_doz_pending_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Days to Pending (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_doz_pending/Metro_mean_doz_pending_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Pending (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_doz_pending/Metro_med_doz_pending_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Pending (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_doz_pending/Metro_med_doz_pending_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Pending (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_doz_pending/Metro_med_doz_pending_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Pending (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_doz_pending/Metro_med_doz_pending_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Days to Close (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_days_to_close/Metro_mean_days_to_close_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Days to Close (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_days_to_close/Metro_mean_days_to_close_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Days to Close (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_days_to_close/Metro_mean_days_to_close_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Days to Close (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_days_to_close/Metro_mean_days_to_close_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Close (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_days_to_close/Metro_median_days_to_close_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Close (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_days_to_close/Metro_median_days_to_close_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Close (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_days_to_close/Metro_median_days_to_close_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Days to Close (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/median_days_to_close/Metro_median_days_to_close_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Smooth, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Smooth, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Smooth, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Smooth, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Raw, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Share of Listings With a Price Cut (Raw, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/perc_listings_price_cut/Metro_perc_listings_price_cut_uc_sfr_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Raw, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Raw, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfr_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Smoothed, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Smoothed, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Smoothed, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut ($, Smoothed, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_amt/Metro_mean_listings_price_cut_amt_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Raw, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Raw, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfr_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Smoothed, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_v2/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfr_smoothed_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Smoothed, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_v2/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfr_smoothed_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Smoothed, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Mean Price Cut (%, Smoothed, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/mean_listings_price_cut_perc/Metro_mean_listings_price_cut_perc_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Raw, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Raw, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfr_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Smoothed, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Smoothed, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Smoothed, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut ($, Smoothed, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_amt/Metro_med_listings_price_cut_amt_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Raw, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfr_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Raw, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfr_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Smoothed, SFR Only, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfr_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Smoothed, SFR Only, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfr_sm_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Raw, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfrcondo_week.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Smoothed, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfrcondo_sm_month.csv',
                    },
                ],
            },
            {
                dataType: 'Median Price Cut (%, Smoothed, All Homes, Weekly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/med_listings_price_cut_perc/Metro_med_listings_price_cut_perc_uc_sfrcondo_sm_week.csv',
                    },
                ],
            },
        ],
    },
    // Order 6: MARKET HEAT INDEX (same order as DAYS ON MARKET, follows in sequence)
    {
        title: 'Market heat index',
        paragraphs: [
            `The <b>market heat index</b> is a time series dataset that aims to capture the balance of for-sale supply and demand in a given market. A higher number means the market is more tilted in favor of sellers. It relies on a combination of engagement and listing performance inputs to provide insights into current market dynamics. It is calculated for single-family and condo homes. For more information, see <a href="https://www.zillow.com/research/market-heat-index-methodology-34057/">methodology</a>.`,
        ],
        order: 6,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType: 'Market Heat Index (All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/market_temp_index/Metro_market_temp_index_uc_sfrcondo_month.csv',
                    },
                ],
            },
        ],
    },
    // Order 7: NEW CONSTRUCTION
    {
        title: 'New construction',
        paragraphs: [
            `<b>New Construction Sales Count: </b>The number of unique new construction homes that sold during the month. Available for the raw cut of single family residence and condo.`,
            `<b>New Construction Median/Mean Sale Price: </b>The median/mean sale price at which new construction homes across various geographies were sold during the month.`,
            `<b>New Construction Median Sale Price Per Sqft: </b>The median sale price divided by square footage calculated over all new construction homes across various geographies were sold during the month.`,
            `Currently, new construction sales metrics are available at a monthly cadence.`,
            `<b>Note: </b>Starting from Aug 2025, the definition of new construction sales has been updated to include homes at all stages.`,
        ],
        order: 7,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType:
                    'New Construction Sales Count (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_con_sales_count_raw/Metro_new_con_sales_count_raw_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'New Construction Median Sale Price (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_con_median_sale_price/Metro_new_con_median_sale_price_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'New Construction Median Sale Price Per Square Foot (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_con_median_sale_price_per_sqft/Metro_new_con_median_sale_price_per_sqft_uc_sfrcondo_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'New Construction Mean Sale Price (Raw, All Homes, Monthly)',
                geographies: [
                    {
                        label: 'Metro & U.S.',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_con_mean_sale_price/Metro_new_con_mean_sale_price_uc_sfrcondo_month.csv',
                    },
                ],
            },
        ],
    },
    // Order 7: AFFORDABILITY (same order as NEW CONSTRUCTION, follows in sequence)
    {
        title: 'Affordability',
        paragraphs: [
            `<b>New Homeowner Income Needed: </b>An estimate of the annual household income required to spend less than 30% of monthly income on the total monthly payment after newly purchasing the typical home with a 20% down payment.`,
            `<b>New Renter Income Needed: </b>An estimate of the household income required to spend less than 30% of monthly income to newly lease the typical rental.`,
            `<b>Affordable Home Price: </b>An estimate of the home price such that the total monthly payment on such a home would not exceed 30% of the median household's monthly income with a 20% down payment.`,
            `<b>Years to Save: </b>A measure of the number of years it would take the median household to save for a 20% down payment on a home, assuming they are able to save 10% of their income into a simple savings account accruing no interest. This is equivalent to the number of years it would take the median household to save for a 10% down payment, assuming a 5% savings rate.`,
            `<b>New Homeowner Affordability: </b>A measure of the share of income the median household would spend on a newly purchased home, including its mortgage payment, homeowner's insurance, property taxes, and maintenance costs. Typically, spending more than 30% of income on housing is considered unaffordable.`,
            `<b>New Renter Affordability: </b>A measure of the share of income the median household would spend to newly lease the typical rental. Typically, spending more than 30% of income on housing is considered unaffordable.`,
        ],
        order: 7,
        dataTypeLabel: 'Data Type',
        geographyLabel: 'Geography',
        dropdownOptions: [
            {
                dataType:
                    'New Homeowner Income Needed: 20% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_homeowner_income_needed/Metro_new_homeowner_income_needed_downpayment_0.20_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'New Renter Income Needed: All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_renter_income_needed/Metro_new_renter_income_needed_uc_sfrcondomfr_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Affordable Home Price: 20% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/affordable_price/Metro_affordable_price_downpayment_0.20_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'Years to Save: 20% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/years_to_save/Metro_years_to_save_downpayment_0.20_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'New Homeowner Affordability: 20% down, All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_homeowner_affordability/Metro_new_homeowner_affordability_downpayment_0.20_uc_sfrcondo_tier_0.33_0.67_sm_sa_month.csv',
                    },
                ],
            },
            {
                dataType:
                    'New Renter Affordability: All Homes, Smoothed & Seasonally Adjusted Time Series',
                geographies: [
                    {
                        label: 'Metro & US',
                        url: 'https://files.zillowstatic.com/research/public_csvs/new_renter_affordability/Metro_new_renter_affordability_uc_sfrcondomfr_sm_sa_month.csv',
                    },
                ],
            },
        ],
    },
];
